import {
  DEFAULT_VOICE_ACTIVITY_OPTIONS,
  type VoiceActivityDetectorOptions,
} from './voice-activity-options';

/**
 * One periodic microphone measurement for automatic stopping. It contains
 * derived volume and frequency values, not separately recorded audio.
 */
export type VoiceActivityFrame = {
  /** Wall-clock time in milliseconds, used to measure recording and silence duration */
  nowMs: number;
  /** Normalized waveform volume from zero through one */
  rms: number;
  /** Share of frequency energy inside the configured speech band, from zero through one */
  voiceScore: number;
};

/**
 * Boundaries of the frequency ranges shown in development diagnostics. Keeping
 * 85–250 Hz separate shows how much car or ventilation rumble the default
 * speech band ignores. The saved Voice settings independently choose the range
 * used for speech scoring.
 */
export const SPECTRUM_BAND_EDGES_HZ = [85, 250, 3_400];

/** Measure waveform volume here so the capture controller only coordinates recording */
export function getWaveformRms(waveformData: Uint8Array): number {
  let squaredAmplitudeSum = 0;
  for (const value of waveformData) {
    // Web Audio stores waveform values as unsigned bytes centered at 128
    const normalized = (value - 128) / 128;
    squaredAmplitudeSum += normalized * normalized;
  }
  return Math.sqrt(squaredAmplitudeSum / waveformData.length);
}

/**
 * Calculate how the sample's energy is distributed across the configured
 * frequency ranges. Using proportions instead of raw volume helps broad noise
 * score lower than sound concentrated in normal speech frequencies.
 */
export function getBandEnergyRatios(
  frequencyData: Uint8Array,
  sampleRate: number,
  bandEdgesHz = SPECTRUM_BAND_EDGES_HZ
): number[] {
  const hzPerBin = sampleRate / 2 / frequencyData.length;
  const bandEnergies = new Array<number>(bandEdgesHz.length + 1).fill(0);
  let totalEnergy = 0;

  for (let index = 0; index < frequencyData.length; index += 1) {
    const energy = frequencyData[index] ** 2;
    totalEnergy += energy;

    const frequency = index * hzPerBin;
    const band = bandEdgesHz.findIndex((edge) => frequency < edge);
    bandEnergies[band === -1 ? bandEdgesHz.length : band] += energy;
  }

  if (totalEnergy === 0) return bandEnergies;
  return bandEnergies.map((energy) => energy / totalEnergy);
}

/** Return the share of spectrum energy inside the configured speech band */
export function getVoiceScore(
  frequencyData: Uint8Array,
  sampleRate: number,
  minimumVoiceFrequencyHz: number,
  maximumVoiceFrequencyHz: number
): number {
  return getBandEnergyRatios(frequencyData, sampleRate, [
    minimumVoiceFrequencyHz,
    maximumVoiceFrequencyHz,
  ])[1];
}

/**
 * `continue` takes another sample, `stop_after_speech` submits the recording,
 * and `stop_without_speech` discards it without transcription.
 */
export type VoiceActivityDecision =
  | 'continue'
  | 'stop_after_speech'
  | 'stop_without_speech';

/**
 * Stateful automatic-stop policy for one recording; create a new instance for
 * the next. It detects only energy in speech frequencies, not who is speaking,
 * so other applications' audio can trigger it when played through speakers.
 */
export class VoiceActivityDetector {
  private startedAtMs: number | null = null;
  private speechFrameCount = 0;
  private trailingSilenceStartedAtMs: number | null = null;

  constructor(
    private readonly options: VoiceActivityDetectorOptions = DEFAULT_VOICE_ACTIVITY_OPTIONS
  ) {}

  /** Incorporate one microphone measurement and choose the recording action */
  addFrame(frame: VoiceActivityFrame): VoiceActivityDecision {
    this.startedAtMs ??= frame.nowMs;
    const elapsedMs = frame.nowMs - this.startedAtMs;
    const isSpeechFrame =
      frame.rms >= this.options.speechRmsThreshold &&
      frame.voiceScore >= this.options.minimumVoiceScore;

    if (isSpeechFrame) {
      // Ordinary speech can dip below the thresholds between syllables, so a
      // few qualifying frames may confirm speech without being consecutive
      this.speechFrameCount += 1;
      this.trailingSilenceStartedAtMs = null;
    }

    const hasConfirmedSpeech =
      this.speechFrameCount >= this.options.minSpeechFramesForUtterance;
    if (!isSpeechFrame && hasConfirmedSpeech) {
      this.trailingSilenceStartedAtMs ??= frame.nowMs;
    }

    if (!hasConfirmedSpeech && elapsedMs >= this.options.noSpeechMs) {
      return 'stop_without_speech';
    }

    if (elapsedMs >= this.options.maxRecordingMs) {
      // The hard limit submits whatever has been captured, even mid-sentence,
      // so a noisy room cannot leave the microphone open indefinitely
      return hasConfirmedSpeech ? 'stop_after_speech' : 'stop_without_speech';
    }

    if (
      hasConfirmedSpeech &&
      elapsedMs >= this.options.minRecordingMs &&
      this.trailingSilenceStartedAtMs !== null &&
      // Use elapsed time because hidden browser windows may sample less often
      frame.nowMs - this.trailingSilenceStartedAtMs >=
        this.options.trailingSilenceMs
    ) {
      return 'stop_after_speech';
    }

    return 'continue';
  }

  /** Time when silence after confirmed speech began; null before confirmation or while hearing speech */
  getTrailingSilenceStartedAtMs(): number | null {
    return this.trailingSilenceStartedAtMs;
  }
}
